<template>
  <section class="px-4 py-10 sm:px-6 lg:px-10 text-black" style="font-family: Verdana, sans-serif;">
    <div class="mx-auto max-w-6xl space-y-4">
      <div class="grid sm:grid-cols-2 gap-4 flex items-center justify-between">
        <div>
          <img
            :src="logoUrl"
            alt="Foresters"
            class="h-auto max-h-28 w-auto"
          />
        </div>
        <div v-if="step === 1" class="flex items-end justify-center sm:justify-end gap-2">
          <Dropdown
            v-if="store.region === 'ca'"
            v-model="uiLanguageSelection"
            :options="uiLanguageOptions"
            option-label="label"
            option-value="value"
            class="w-36"
            @update:modelValue="setUiLanguage"
          />
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <div v-if="step === 1" class="flex items-start justify-between gap-4">
          <div class="space-y-3">
            <div class="space-y-1">
              <h1 class="text-4xl font-bold text-slate-900 sm:text-5xl">
                {{ headings.title }}
              </h1>
              <p class="text-2xl">
                {{ headings.subtitle }}
              </p>
            </div>
            <div class="max-w-6xl space-y-3 text-black">
              <p v-for="line in introLines" :key="line" v-html="line"></p>
            </div>
          </div>
        </div>

      </div>

      <div class="card-surface space-y-8 p-6 sm:p-8">
        

        <template v-if="step === 1">
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-slate-900">
              {{ uiCopy.steps.step1Title }}
            </h2>
            <p class="text-sm text-black">
              {{ uiCopy.steps.step1Desc }}
            </p>
          </div>

          <div class="grid gap-6 lg:grid-cols-3">
            <div class="space-y-4 col-span-1">
              <label class="field-label" for="producer-id">{{
                uiCopy.labels.producerId
              }}</label>
              <InputText
                id="producer-id"
                ref="producerIdInput"
                v-model.trim="store.pageOne.producerId"
                class="w-full"
                @keydown.enter.prevent="focusStateSelect"
              />
            </div>

            <div v-if="isUsFlow" class="space-y-3 col-span-2">
              <p class="field-label">
                {{ uiCopy.labels.licensesQuestion }}
              </p>
              <MultiSelect
                ref="stateSelectInput"
                v-model="store.pageOne.licenses"
                :options="usLicenses"
                option-label="label"
                option-value="value"
                placeholder="Select states"
                display="chip"
                class="w-full"
                :panelClass="'state-multiselect-panel'"
                filter
                resetFilterOnClear
                autoFilterFocus
                @change="hideStateSelect"
              />
              <p class="text-xs text-slate-600">
                Products in the Foresters Financial portfolio are not available in the state of New York.
              </p>
            </div>

            <div v-else class="space-y-3 col-span-2">
              <p class="field-label">{{ uiCopy.labels.contractingQuestion }}</p>
              <div class="grid gap-3 sm:grid-cols-3">
                <label
                  v-for="option in contractingOptions"
                  :key="option.value"
                  class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800"
                >
                  <RadioButton
                    :input-id="`contracting-${option.value}`"
                    name="contracting"
                    :value="option.value"
                    :model-value="store.pageOne.contracting"
                    @change="setContracting(option.value)"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
          </div>

          <div
            v-if="showStepOneAction"
            class="mobile-action-footer flex justify-center items-center pt-4"
          >
            <!-- <div class="text-sm text-slate-500">
              Step 2: Required fields marked with *
            </div> -->
            <Button
              :label="uiCopy.buttons.submit"
              icon="pi pi-arrow-right"
              icon-pos="right"
              class="fbg fbr"
              :disabled="!validateStepOne()"
              :loading="loading"
              @click="handleStepOne"
            />
          </div>
        </template>

        <template v-else-if="step === 2">
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-slate-900">
              {{ uiCopy.steps.step2Title }}
            </h2>
            <p class="text-sm text-black">
              {{ uiCopy.steps.step2Desc }}
            </p>
          </div>
          <div class="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              class="h-full rounded-full fbg transition-all"
              :style="{ width: progressWidth }"
            />
          </div>

          <div class="grid gap-6 lg:grid-cols-2">
            <div class="space-y-4">
              <div class="space-y-2">
                <p class="field-label">{{ uiCopy.labels.productsQuestion }}</p>
                <div class="grid gap-3">
                  <div
                    v-for="product in availableProducts"
                    :key="product.key"
                    class="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <RadioButton
                          v-if="isUsFlow"
                          :input-id="`product-${product.key}`"
                          name="product"
                          :value="product.key"
                          :model-value="store.pageTwo.selectedProducts[0]"
                          @change="toggleProductSelection(product.key)"
                        />
                        <Checkbox
                          v-else
                          :input-id="`product-${product.key}`"
                          binary
                          :model-value="isProductSelected(product.key)"
                          :disabled="isProductDisabled(product.key)"
                          @update:modelValue="toggleProductSelection(product.key)"
                        />
                        <label
                          :for="`product-${product.key}`"
                          class="text-sm font-semibold text-slate-800"
                        >
                          {{ product.label }}
                        </label>
                      </div>
                      <!-- <span class="text-xs text-slate-500">{{
                        productBadge(product.key)
                      }}</span> -->
                    </div>
                    <div
                      v-if="
                        isUsFlow &&
                        isProductSelected(product.key) &&
                        productRestrictionNotes(product.key).length
                      "
                      class="space-y-1 text-xs text-slate-600"
                    >
                      <p
                        v-for="line in productRestrictionNotes(product.key)"
                        :key="line"
                      >
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="grid gap-6">
              <div class="space-y-4">
                <div>
                  <label class="field-label" for="name"
                    >{{ uiCopy.labels.name }}</label
                  >
                  <InputText
                    id="name"
                    ref="nameInput"
                    v-model.trim="store.pageTwo.name"
                    class="w-full"
                    @blur="markFieldTouched('name')"
                    @keydown.enter.prevent="focusPhoneInput"
                  />
                  <p v-if="fieldTouched.name && !store.pageTwo.name" class="mt-1 text-xs font-semibold text-rose-700">
                    Please enter a name.
                  </p>
                </div>
                <div>
                  <label class="field-label" for="phone">{{
                    uiCopy.labels.phone
                  }}</label>
                  <InputText
                    id="phone"
                    ref="phoneInput"
                    :model-value="store.pageTwo.phone"
                    class="w-full"
                    maxlength="12"
                    @update:modelValue="updatePhone"
                    @blur="markFieldTouched('phone')"
                    @keydown.enter.prevent="focusEmailInput"
                  />
                  <p class="mt-1 text-xs text-slate-500">
                    {{ uiCopy.labels.phoneHelp }}
                  </p>
                  <p v-if="fieldTouched.phone && !isPhoneValid" class="mt-1 text-xs font-semibold text-rose-700">
                    Please enter a valid phone number.
                  </p>
                </div>
                <div>
                  <label class="field-label" for="email">{{
                    uiCopy.labels.email
                  }}</label>
                  <InputText
                    id="email"
                    ref="emailInput"
                    v-model.trim="store.pageTwo.email"
                    class="w-full"
                    @blur="markFieldTouched('email')"
                    @keydown.enter.prevent="focusNextAfterEmail"
                  />
                  <p v-if="fieldTouched.email && !isEmailValid" class="mt-1 text-xs font-semibold text-rose-700">
                    Please enter a valid email address.
                  </p>
                </div>
                <div v-if="store.requiresCaLicense">
                  <label class="field-label" for="ca-license"
                    >{{ uiCopy.labels.caLicense }}</label
                  >
                  <InputText
                    id="ca-license"
                    ref="caLicenseInput"
                    v-model.trim="store.pageTwo.caLicenseNumber"
                    class="w-full"
                    @keydown.enter.prevent="blurActiveElement"
                  />
                </div>
              </div>

              <div class="space-y-4">
                <div>
                  <p class="field-label">{{ uiCopy.labels.languageSelection }}</p>
                  <div class="flex flex-wrap gap-3">
                    <label
                      v-for="option in languageOptions"
                      :key="option.key"
                      class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800"
                    >
                      <RadioButton
                        :input-id="`lang-${option.key}`"
                        name="language"
                        :value="option.key"
                        :model-value="selectedLanguageKey"
                        @change="selectLanguage(option.key)"
                      />
                      {{ option.label }}
                    </label>
                  </div>
                </div>
                <div>
                  <p class="field-label">{{ uiCopy.labels.narratorSelection }}</p>
                  <div class="flex flex-wrap gap-3">
                    <label
                      v-for="option in narratorOptions"
                      :key="option.value"
                      class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800"
                    >
                      <RadioButton
                        :input-id="`narrator-${option.value}`"
                        name="narrator"
                        :value="option.value"
                        :model-value="store.pageTwo.narrator"
                        @change="selectNarrator(option.value)"
                      />
                      {{ option.label }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between justify-center w-full"
          >
            <div
              class="grid grid-cols-1 gap-6 w-full items-center justify-center"
            >
              <!-- <span class="self-center text-sm text-slate-500"
                >Outputs up to 4 videos.</span
              > -->
              <Button
                :label="uiCopy.buttons.requestVideo"
                :loading="loading"
                icon="pi pi-check"
                class="fbg fbr max-w-72 mx-auto"
                icon-pos="right"
                size="large"
                :disabled="!validateStepTwo()"
                @click="handleConfirmStep"
              />
              <Button
                :label="uiCopy.buttons.back"
                severity="secondary"
                outlined
              class="fbr max-w-50 mx-auto !bg-white"
              :disabled="loading"
              @click="setStep(1)"
            />
            </div>
          </div>
        </template>

        <template v-else-if="step === 3">
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-slate-900">
              {{ uiCopy.steps.step3Title }}
            </h2>
            <p class="text-sm text-black">
              {{ uiCopy.steps.step3Desc }}
            </p>
          </div>
          <div class="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              class="h-full rounded-full fbg transition-all"
              :style="{ width: progressWidth }"
            />
          </div>

          <div class="grid gap-6 lg:grid-cols-2">
            <div class="space-y-4">
              <div class="space-y-2">
                <p class="field-label">{{ uiCopy.labels.selectedProducts }}</p>
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p v-for="label in selectedProductLabels" :key="label">
                    {{ label }}
                  </p>
                </div>
              </div>

              <div class="space-y-2">
                <p class="field-label">
                  {{ uiCopy.labels.languageNarratorSelection }}
                </p>
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p v-for="label in selectedLanguageLabels" :key="label">
                    {{ label }} - {{ selectedNarratorLabel }}
                  </p>
                </div>
              </div>
              <div v-if="isUsFlow" class="space-y-2">
                <p class="field-label">{{ uiCopy.labels.selectedStates }}</p>
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p v-for="state in store.pageOne.licenses" :key="state">
                    {{ state }}
                  </p>
                </div>
              </div>

            </div>

            <div class="space-y-4">
              <div class="space-y-2">
                <p class="field-label">{{ uiCopy.labels.personalizationTitle }}</p>
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-2">
                  <div class="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {{ uiCopy.labels.nameLabel }}
                    </span>
                    <span>{{ store.pageTwo.name }}</span>
                  </div>
                  <div class="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {{ uiCopy.labels.phoneLabel }}
                    </span>
                    <span>{{ store.pageTwo.phone }}</span>
                  </div>
                  <div class="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {{ uiCopy.labels.emailLabel }}
                    </span>
                    <span>{{ store.pageTwo.email }}</span>
                  </div>
                  <div v-if="store.requiresCaLicense" class="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {{ uiCopy.labels.caLicenseLabel }}
                    </span>
                    <span>{{ store.pageTwo.caLicenseNumber }}</span>
                  </div>
                </div>
              </div>
              <div v-if="isUsFlow" class="space-y-2">
                <p class="field-label">{{ uiCopy.labels.computedOutputs }}</p>
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-2">
                  <p class="text-xs text-slate-500">
                    {{ uiCopy.labels.totalVideosPrefix }} {{ confirmationVariants.length }}
                  </p>
                  <div v-for="variant in confirmationVariants" :key="variant.id">
                    <p class="font-semibold text-slate-800">
                      {{ productLabelForVariant(variant) }}
                    </p>
                    <p class="text-xs text-slate-600">
                      {{ variantDisplayLabel(variant) }} - {{ narratorLabel(variant.narrator) }}
                    </p>
                    <p v-if="variant.complianceCode" class="text-xs text-slate-600">
                      {{ uiCopy.labels.complianceCodePrefix }} {{ variant.complianceCode }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between justify-center w-full"
          >
            <div
              class="grid grid-cols-1 gap-6 w-full items-center justify-center"
            >
              <label v-if="store.region === 'ca'" class="flex items-start gap-3 text-sm text-slate-700">
                <Checkbox
                  input-id="casl-ack"
                  binary
                  :model-value="store.pageTwo.caslAcknowledged"
                  @update:modelValue="updateCaslAcknowledgement"
                />
                <span v-html="uiCopy.labels.caslAcknowledgement"></span>
              </label>
              <Button
                :label="uiCopy.buttons.confirm"
                :loading="loading"
                icon="pi pi-check"
                class="fbg fbr max-w-72 mx-auto"
                icon-pos="right"
                size="large"
                :disabled="!validateStepTwo() || (store.region === 'ca' && !store.pageTwo.caslAcknowledged)"
                @click="handleSubmit"
              />
              <Button
                :label="uiCopy.buttons.back"
                severity="secondary"
                outlined
              class="fbr max-w-50 mx-auto !bg-white"
              :disabled="loading"
                @click="setStep(2)"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-slate-900">
              {{ resultsCopy.headline }}
            </h2>
            <p class="text-sm text-black">{{ resultsCopy.subhead }}</p>
            <p v-if="resultsCopy.socialNote" class="text-sm text-black">
              {{ resultsCopy.socialNote }}
            </p>
            <div
              v-if="resultsCopy.socialSample"
              class="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700 whitespace-pre-line"
            >
              {{ resultsCopy.socialSample }}
            </div>
          </div>

          <div class="grid gap-4 lg:grid-cols-2">
            <div
              v-for="variant in store.variants"
              :key="variant.id"
              class="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 max-w-md mx-auto"
            >
              <div class="flex items-start justify-between gap-2">
                <div>
                  <p class="text-sm font-semibold text-slate-700">
                    {{ productLabelForVariant(variant) }}
                  </p>
                  <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--p-primary-color)]">
                    {{ variantDisplayLabel(variant) }}
                  </p>
                  <p class="text-xs text-slate-500">
                    {{ uiCopy.labels.narratorPrefix }}:
                    {{ narratorLabel(variant.narrator) }}
                  </p>
                  <p v-if="variantRegionLabel(variant)" class="text-xs text-slate-500">
                    Region: {{ variantRegionLabel(variant) }}
                  </p>
                </div>
              </div>
              <div
                class="relative overflow-hidden rounded-lg text-xs text-slate-700 break-words"
              >
                <!-- {{ variant.url }} -->
                  <img :src="variant.image || 'https://www.biblecenterchurch.com/wp-content/uploads/2018/10/video-placeholder-4453_1080x675.png'" alt="Video placeholder" class="w-full h-auto">
                  <div class="absolute left-3 top-3 max-w-[80%] rounded bg-white/95 px-3 py-1 text-sm font-bold text-slate-900 shadow-sm">
                    {{ productLabelForVariant(variant) }}
                  </div>
              </div>
              <!-- <div class="text-xs text-slate-500">{{ copyEmail(variant) }}</div> -->

              <!-- <div class="text-xs text-slate-500">{{ copyEmail(variant) }}</div> -->
              <div v-if="!isMobileDevice" class="grid grid-cols-2 gap-2">
                <Button
                  :label="uiCopy.buttons.sendEmail"
                  size="large"
                  outlined
                  class="fbr !text-white fbg w-full"
                  @click="openEmail(variant)"
                />
                <Button
                  :label="uiCopy.buttons.copyLink"
                  size="large"
                  severity="success"
                  class="w-full"
                  @click="copySocial(variant)"
                />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <Button
                  size="large"
                  outlined
                  severity="secondary"
                  class="!bg-white !text-white !border-gray-300"
                  @click="shareSocial('facebook', variant)"
                > 
                <img src="https://mspb.b-cdn.net/fb.png" alt="Facebook" class="max-h-4">
                </Button>
                <Button
                  size="large"
                  outlined
                  severity="secondary"
                  class="!bg-white !text-black !border-gray-300"
                  @click="shareSocial('linkedin', variant)"
                >
                {{ uiCopy.buttons.share }}
                <img src="https://cdn-icons-png.flaticon.com/512/174/174857.png" alt="LinkedIn" class="max-h-4"></img>
                </Button>
                <!-- <Button
                  size="small"
                  outlined
                  class="!bg-black !text-white !border-black"
                  @click="shareSocial('x', variant)"
                >
                {{ uiCopy.buttons.shareOn }}<img src="https://mspb.b-cdn.net/xlogo.png" alt="X" class="w-5 h-5 -ml-2">
                </Button> -->
              </div>
              <Button
                v-if="canUseNativeShare"
                :label="uiCopy.buttons.share"
                size="large"
                outlined
                severity="secondary"
                class="!bg-white !text-black !border-gray-300"
                @click="shareNative(variant)"
              />
              <p v-if="canUseNativeShare" class="text-xs font-semibold text-amber-700">
                Sharing is supported for Facebook and LinkedIn only.
              </p>
              <div
                v-if="isUsFlow && productRestrictionNotes(variant.productKey).length"
                class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800"
              >
                <p
                  v-for="line in productRestrictionNotes(variant.productKey)"
                  :key="`${variant.id}-${line}`"
                >
                  {{ line }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="store.variants.length === 0"
            class="rounded-lg bg-amber-50 p-4 text-sm text-amber-800"
          >
            {{ uiCopy.labels.noVideos }}
          </div>

          <div class="flex items-right justify-end items-center pt-4 gap-4">
            <div class="text-sm text-black">
              {{ uiCopy.labels.startOver }}
            </div>
            <Button
              :label="uiCopy.buttons.back"
              outlined
              class="fbr fbt !bg-white"
              @click="setStep(3)"
            />
            <Button
              :label="uiCopy.buttons.startAgain"
              outlined
              class="fbr fbt !bg-white"
              @click="resetFlow"
            />
          </div>
        </template>

        <div class="space-y-2 pt-6">
          <div
            class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700 whitespace-pre-line"
          >
            <div v-html="disclosures"></div>
          </div>
        </div>
      </div>
    </div>
    <Toast />
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import InputText from "primevue/inputtext";
import Toast from "primevue/toast";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import { useToast } from "primevue/usetoast";
import { useVideoFlowStore } from "@/front/stores/videoFlowStore";
import {
  HEADINGS,
  UI_COPY,
  INTRO_COPY,
  DISCLOSURES,
  RESULTS_COPY,
  EMAIL_TEMPLATE,
  PRODUCT_EMAIL_TEMPLATES,
  EMAIL_DISCLAIMERS,
  EMAIL_PRODUCT_DISCLOSURES,
  EMAIL_COMPLIANCE_CODES,
  SOCIAL_TEMPLATE,
} from "@/front/constants/copy";
import { LANGUAGE_OPTIONS, US_PRODUCT_RULES, US_STATES } from "@/front/constants/rules";
import { postSubmission } from "@/front/services/submissions";

const store = useVideoFlowStore();
const toast = useToast();
const route = useRoute();
const step = ref(1);
const loading = ref(false);
const selectedLanguageKey = ref("en");
const uiLanguageSelection = ref("en");
const producerIdInput = ref(null);
const stateSelectInput = ref(null);
const nameInput = ref(null);
const phoneInput = ref(null);
const emailInput = ref(null);
const caLicenseInput = ref(null);
const fieldTouched = ref({
  name: false,
  phone: false,
  email: false,
});

const usLicenses = US_STATES.map((state) => ({ value: state, label: state }));

const uiLanguageOptions = computed(() =>
  store.region === "ca"
    ? [
        { value: "en", label: "English" },
        { value: "fr", label: "Français" },
      ]
    : [
        { value: "en", label: "English" },
        { value: "es", label: "Español" },
      ]
);

const uiCopy = computed(() => {
  if (store.region === "ca") {
    return UI_COPY.ca[store.uiLanguage] || UI_COPY.ca.en;
  }
  return UI_COPY.us[store.uiLanguage] || UI_COPY.us.en;
});

const headings = computed(() => {
  if (store.region === "ca") {
    return HEADINGS.ca[store.uiLanguage] || HEADINGS.ca.en;
  }
  return HEADINGS.us[store.uiLanguage] || HEADINGS.us.en;
});
const resultsCopy = computed(() => {
  if (store.region === "ca") {
    return RESULTS_COPY.ca[store.uiLanguage] || RESULTS_COPY.ca.en;
  }
  return RESULTS_COPY.us[store.uiLanguage] || RESULTS_COPY.us.en;
});
const emailTemplateForLanguage = (language) => {
  const templates = EMAIL_TEMPLATE?.[store.region] || {};
  return templates[language] || templates.en || EMAIL_TEMPLATE?.us?.en;
};

const isMobileDevice = computed(() => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  return window.matchMedia?.("(max-width: 767px)")?.matches
    || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});
const isDev = computed(() => window.location.href.includes("localhost") || window.location.href.includes("dev.mindstamp.com"));
const canUseNativeShare = computed(() =>
  isMobileDevice.value && typeof navigator !== "undefined" && Boolean(navigator.share)
);

const productTemplatesForLanguage = (language) =>
  PRODUCT_EMAIL_TEMPLATES?.[store.region]?.[language] || {};

const emailDisclaimersForLanguage = (language) =>
  EMAIL_DISCLAIMERS?.[store.region]?.[language];
const emailProductDisclosuresForLanguage = (language) =>
  EMAIL_PRODUCT_DISCLOSURES?.[store.region]?.[language] || {};
const complianceCodeForLanguage = (language, productKey) =>
  EMAIL_COMPLIANCE_CODES?.[store.region]?.[language]?.[productKey];
const socialTemplate = computed(() => {
  if (store.region === "ca") {
    return SOCIAL_TEMPLATE.ca[store.uiLanguage] || SOCIAL_TEMPLATE.ca.en;
  }
  return SOCIAL_TEMPLATE.us[store.uiLanguage] || SOCIAL_TEMPLATE.us.en;
});

const contractingOptions = computed(() => [
  { value: "foresters", label: uiCopy.value.contractingOptions.foresters },
  { value: "cpp", label: uiCopy.value.contractingOptions.cpp },
  { value: "both", label: uiCopy.value.contractingOptions.both },
]);

const languageOptions = computed(() =>
  LANGUAGE_OPTIONS[store.region].map((option) => {
    const key = option.value.join("-");
    return {
      ...option,
      key,
      label: uiCopy.value.languageOptions[key] || option.label,
    };
  })
);
const preferredLanguageOption = computed(() => {
  if (store.region === "us" && store.uiLanguage === "es") {
    return languageOptions.value.find((option) => option.key === "es") || languageOptions.value[0];
  }
  return languageOptions.value[0];
});

const introLines = computed(() => {
  if (store.region === "ca") {
    return INTRO_COPY.ca[store.uiLanguage] || INTRO_COPY.ca.en;
  }
  return INTRO_COPY.us[store.uiLanguage] || INTRO_COPY.us.en;
});

const disclosures = computed(() => {
  if (store.region === "us") {
    const disclosureLanguage =
      store.pageTwo.selectedLanguages.length === 1 ? store.pageTwo.selectedLanguages[0] : "en";
    return DISCLOSURES.us[disclosureLanguage] || DISCLOSURES.us.en;
  }
  return DISCLOSURES.ca[store.uiLanguage] || DISCLOSURES.ca.en;
});
const narratorOptions = computed(() => [
  { value: "male", label: uiCopy.value.narratorOptions.male },
  { value: "female", label: uiCopy.value.narratorOptions.female },
]);
const logoUrl = computed(() => {
  if (store.region === "ca" && store.uiLanguage === "fr") {
    return "https://mspb.b-cdn.net/foresters-logo-fr-nobg.png";
  }
  return "https://mspb.b-cdn.net/foresters-logo-en-nobg.png";
});

const uiLanguageLabel = computed(() =>
  uiLanguageOptions.value.find((option) => option.value === store.uiLanguage)?.label || "English"
);
const stepLabel = computed(() => {
  if (step.value === 1) return uiCopy.value.stepLabels.eligibility;
  if (step.value === 2) return uiCopy.value.stepLabels.personalize;
  if (step.value === 3) return uiCopy.value.stepLabels.confirm;
  return uiCopy.value.stepLabels.results;
});
const progressSteps = computed(() => [
  { value: 1, label: uiCopy.value.stepLabels.eligibility },
  { value: 2, label: uiCopy.value.stepLabels.personalize },
  { value: 3, label: uiCopy.value.stepLabels.confirm },
  { value: 4, label: uiCopy.value.stepLabels.results },
]);
const progressWidth = computed(() => `${(step.value / 4) * 100}%`);
const isUsFlow = computed(() => store.region === "us");
const showStepOneAction = computed(
  () => !isUsFlow.value || store.pageOne.licenses.length > 0
);

const availableProducts = computed(() => {
  const products = store.availableProducts;
  const labels = uiCopy.value.productLabels || {};
  if (!Object.keys(labels).length) return products;
  return products.map((product) => ({
    ...product,
    label: labels[product.key] || product.label,
  }));
});
const selectedProductLabels = computed(() =>
  store.pageTwo.selectedProducts.map((key) => {
    const match = availableProducts.value.find((product) => product.key === key);
    return match ? match.label : key;
  })
);
const selectedLanguageLabels = computed(() =>
  store.pageTwo.selectedLanguages.map((lang) => languageLabel(lang))
);
const selectedNarratorLabel = computed(() =>
  narratorLabel(store.pageTwo.narrator)
);
const productNotes = (productKey) =>
  store.region === "us" ? US_PRODUCT_RULES?.[productKey]?.noteLines || [] : [];
const productRestrictionNotes = (productKey) => productNotes(productKey);

const confirmationVariants = computed(() =>
  validateStepTwo() ? store.buildVariants({ persist: false }) : []
);

const showRequiredError = () =>
  toast.add({
    severity: "warn",
    summary: uiCopy.value.errors.required,
    life: 3000,
  });

const setContracting = (value) => {
  store.updatePageOne({ contracting: value });
};

const focusField = (fieldRef) => {
  const target =
    fieldRef.value?.$el?.querySelector?.("input, button, [tabindex]") ||
    fieldRef.value?.$el ||
    fieldRef.value;
  target?.focus?.();
};

const blurActiveElement = () => {
  document.activeElement?.blur?.();
};

const focusStateSelect = () => {
  if (!isUsFlow.value) return blurActiveElement();
  focusField(stateSelectInput);
};

const hideStateSelect = () => {
  requestAnimationFrame(() => stateSelectInput.value?.hide?.());
};

const focusPhoneInput = () => focusField(phoneInput);
const focusEmailInput = () => focusField(emailInput);
const focusNextAfterEmail = () => {
  if (store.requiresCaLicense) {
    focusField(caLicenseInput);
    return;
  }
  blurActiveElement();
};

const setUiLanguage = (lang) => {
  if (!lang) return;
  store.setUiLanguage(lang);
  uiLanguageSelection.value = lang;
};

const syncRegionFromRoute = () => {
  const isCanadaRoute =
    route.meta?.region === "ca" || route.path.startsWith("/canada");
  const isWfgRoute =
    route.meta?.variant === "wfg" || route.path.startsWith("/canada/wfg");
  const targetRegion = isCanadaRoute ? "ca" : "us";
  const targetUiLanguage =
    route.meta?.uiLanguage || "en";
  const excludedProducts = isWfgRoute ? ["live-well-plus"] : [];
  const regionChanged = targetRegion !== store.region;
  const excludedChanged =
    excludedProducts.join("|") !== store.excludedProducts.join("|");

  if (regionChanged) {
    store.setRegion(targetRegion);
    setStep(1);
  }
  if (excludedChanged) {
    store.setExcludedProducts(excludedProducts);
  }
  if (store.uiLanguage !== targetUiLanguage) {
    store.setUiLanguage(targetUiLanguage);
  }
};

const isProductSelected = (key) => store.pageTwo.selectedProducts.includes(key);
const isProductDisabled = (key) =>
  !isProductSelected(key) &&
  store.pageTwo.selectedProducts.length >= store.productLimit;
const toggleProductSelection = (key) => {
  store.toggleProduct(key);
};

const productBadge = (key) => {
  if (store.region === "us") return "US";
  return "CA";
};

const selectLanguage = (key) => {
  selectedLanguageKey.value = key;
  const option = languageOptions.value.find((opt) => opt.key === key);
  if (option) {
    store.setLanguages(option.value);
  }
};

const languageLabel = (code) => {
  const labels = uiCopy.value.languageNames;
  if (labels?.[code]) return labels[code];
  return code;
};

const narratorLabel = (value) => {
  if (!value) return uiCopy.value.narratorOptions.male;
  return uiCopy.value.narratorOptions[value] || value;
};

const selectNarrator = (value) => {
  store.updatePageTwo({ narrator: value });
};
const updatePhone = (value) => {
  store.updatePageTwo({ phone: value });
};
const updateCaslAcknowledgement = (value) => {
  store.updatePageTwo({ caslAcknowledged: value });
};

const productLabelForKey = (key, fallback) => {
  const labels = uiCopy.value.productLabels || {};
  return labels[key] || fallback || key;
};

const productLabelForVariant = (variant) =>
  productLabelForKey(variant.productKey, variant.productLabel);

const variantRegionLabel = (variant) => {
  if (variant.region !== "us") return "";
  return variant.versionLabel || "US";
};

const variantDisplayLabel = (variant) => {
  const language = languageLabel(variant.language);
  const region = variantRegionLabel(variant);
  return region ? `${region} ${language}` : language;
};

const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
const validatePhone = (phone) => {
  return /^\d{3}-\d{3}-\d{4}$/.test(phone || "");
};
const isEmailValid = computed(() => validateEmail(store.pageTwo.email));
const isPhoneValid = computed(() => validatePhone(store.pageTwo.phone));
const markFieldTouched = (field) => {
  fieldTouched.value[field] = true;
};

const validateStepOne = () => {
  if (!store.pageOne.producerId.trim()) return false;
  if (isUsFlow.value && store.pageOne.licenses.length === 0) return false;
  if (!isUsFlow.value && !store.pageOne.contracting) return false;
  return true;
};

const validateStepTwo = () => {
  if (store.pageTwo.selectedProducts.length === 0) return false;
  if (!store.pageTwo.name || !store.pageTwo.phone || !store.pageTwo.email)
    return false;
  if (!validatePhone(store.pageTwo.phone)) return false;
  if (!validateEmail(store.pageTwo.email)) return false;
  if (store.requiresCaLicense && !store.pageTwo.caLicenseNumber) return false;
  if (!store.pageTwo.selectedLanguages.length) return false;
  if (!store.pageTwo.narrator) return false;
  return true;
};

const scrollToTop = () => {
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
};

const setStep = (nextStep) => {
  step.value = nextStep;
  scrollToTop();
};

const handleStepOne = () => {
  if (!validateStepOne()) {
    showRequiredError();
    return;
  }
  setStep(2);
};

const handleConfirmStep = () => {
  if (!validateStepTwo()) {
    showRequiredError();
    return;
  }
  setStep(3);
};

const goBack = () => {
  setStep(Math.max(1, step.value - 1));
};

const handleSubmit = async () => {
  if (!validateStepTwo()) {
    showRequiredError();
    return;
  }
  loading.value = true;
  try {
    const variants = store.generateVariants();
    await postSubmission({
      region: store.region,
      producer_id: store.pageOne.producerId,
      licenses: store.region === "us" ? store.pageOne.licenses : [],
      contracting: store.region === "ca" ? store.pageOne.contracting : null,
      ca_license_number: store.requiresCaLicense
        ? store.pageTwo.caLicenseNumber
        : null,
      name: store.pageTwo.name,
      phone: store.pageTwo.phone,
      email: store.pageTwo.email,
      products: store.pageTwo.selectedProducts,
      languages: store.pageTwo.selectedLanguages,
      narrator: store.pageTwo.narrator,
      variant_ids: variants.map((variant) => variant.id),
      variant_count: variants.length,
      metadata: {
        user_locale: navigator.language,
        client_time: new Date().toISOString(),
        page_path: window.location.pathname,
      },
    });
    setStep(4);
  } catch (error) {
    toast.add({
      severity: "error",
      summary: uiCopy.value.toasts.submissionFailedTitle,
      detail: uiCopy.value.toasts.submissionFailedDetail,
      life: 4000,
    });
  } finally {
    loading.value = false;
  }
};

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.add({
      severity: "success",
      summary: `${label} ${uiCopy.value.toasts.copiedSuffix}`,
      life: 2000,
    });
  } catch (error) {
    toast.add({
      severity: "warn",
      summary: uiCopy.value.toasts.copyUnavailable,
      life: 2000,
    });
  }
};

const copyLink = (url) => copyText(url, uiCopy.value.labels.link);

const replaceToken = (text, token, value) =>
  text.replace(new RegExp(`\\[${token}\\]`, "gi"), value);

const applyCaLicenseNumber = (text, caLicenseNumber) => {
  if (caLicenseNumber) {
    return text.replace(/\[xxx\]/gi, caLicenseNumber);
  }
  return text
    .replace(/\n?(?:CA license number|California License Number):?\s*\[xxx\]/gi, "")
    .replace(/\n?Número de licencia en CA:?\s*\[xxx\]/gi, "")
    .replace(/\n?Numéro de licence CA\s*:?\s*\[xxx\]/gi, "");
};

const applyTemplateValues = (
  template,
  productLabel,
  link,
  contactInfo,
  caLicenseNumber
) => {
  const withProduct = replaceToken(
    replaceToken(template, "Product Name", productLabel),
    "nom du produit",
    productLabel
  );
  const withSpanishProduct = replaceToken(
    withProduct,
    "Nombre del Producto",
    productLabel
  );
  const withAgent = withSpanishProduct
    .replace(/\[Agent Name\]/g, store.pageTwo.name || "[Agent Name]")
    .replace(/\[Advisor Name\]/g, store.pageTwo.name || "[Advisor Name]")
    .replace(
      /\[Nom de l[’']agent\]/g,
      store.pageTwo.name || "[Nom de l’agent]"
    )
    .replace(
      /\[Nom du conseiller\]/g,
      store.pageTwo.name || "[Nom du conseiller]"
    )
    .replace(/\[Nombre del Agente\]/g, store.pageTwo.name || "[Nombre del Agente]")
    .replace(/\[Contact Info\]/g, contactInfo || "[Contact Info]")
    .replace(/\[Coordonnées\]/g, contactInfo || "[Coordonnées]")
    .replace(
      /\[Información de contacto\]/g,
      contactInfo || "[Información de contacto]"
    );
  const withLink = replaceToken(
    replaceToken(withAgent, "Insert Video Link", link),
    "lien de la vidéo",
    link
  );
  const withSpanishLink = replaceToken(
    withLink,
    "Insertar enlace al video",
    link
  );
  return applyCaLicenseNumber(withSpanishLink, caLicenseNumber);
};

const cleanEmailVideoLink = (body, link) => {
  const escapedLink = link.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return body
    .replace(new RegExp(`(^|\\n)[ \\t]+(${escapedLink})(?=\\s|$)`, "g"), "$1$2")
    .replace(new RegExp(`([^\\n])(${escapedLink})`, "g"), "$1\n$2")
    .replace(new RegExp(`(${escapedLink})([^\\n\\s])`, "g"), "$1\n$2")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const normalizeMailtoText = (text) =>
  text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/…/g, "...")
    .replace(/®/g, "(R)")
    .replace(/ /g, " ")
    .replace(/\u00a0/g, " ");

const copyEmail = (variant) => {
  const contactInfo = [store.pageTwo.phone, store.pageTwo.email]
    .filter(Boolean)
    .join("\n");
  const productLabel = productLabelForVariant(variant);
  const emailLanguage =
    variant.language || store.pageTwo.selectedLanguages[0] || "en";
  const productTemplates = productTemplatesForLanguage(emailLanguage);
  const template =
    productTemplates[variant.productKey] ||
    emailTemplateForLanguage(emailLanguage);
  const subject = applyTemplateValues(
    template.subject,
    productLabel,
    variant.url,
    contactInfo,
    store.pageTwo.caLicenseNumber
  );
  const bodyWithLink = cleanEmailVideoLink(applyTemplateValues(
    template.body,
    productLabel,
    variant.url,
    contactInfo,
    store.pageTwo.caLicenseNumber
  ), variant.url);
  const body = bodyWithLink.includes(variant.url)
    ? bodyWithLink
    : `${bodyWithLink}\n\n${variant.url}`;
  const emailDisclaimers = emailDisclaimersForLanguage(emailLanguage);
  const productDisclosureEntry =
    emailProductDisclosuresForLanguage(emailLanguage)[variant.productKey];
  const productDisclosures =
    typeof productDisclosureEntry === "object"
      ? productDisclosureEntry[variant.version] || productDisclosureEntry.standard
      : productDisclosureEntry;
  const complianceCode =
    variant.complianceCode ||
    complianceCodeForLanguage(emailLanguage, variant.productKey);
  const extraLines = [emailDisclaimers, productDisclosures, complianceCode]
    .filter(Boolean)
    .join("\n\n");
  const finalBody = extraLines ? `${body}\n\n${extraLines}` : body;
  const mailto = `mailto:?subject=${encodeURIComponent(
    normalizeMailtoText(subject)
  )}&body=${encodeURIComponent(normalizeMailtoText(finalBody))}`;
  return mailto;
  window.location.href = mailto;
  toast.add({
    severity: "info",
    summary: uiCopy.value.toasts.openingMailClient,
    life: 1500,
  });
};

const openEmail = (variant) => {
  const mailto = copyEmail(variant);
  window.location.href = mailto;
  toast.add({
    severity: "info",
    summary: uiCopy.value.toasts.openingMailClient,
    life: 1500,
  });
};

const copySocial = (variant) => {
  const productLabel = productLabelForVariant(variant);
  const body = applyTemplateValues(
    socialTemplate.value,
    productLabel,
    variant.url,
    "",
    null
  );
  copyText(variant.url, uiCopy.value.labels.link);
  // copyText(body, "Social post");
};

const shareSocial = (platform, variant) => {
  const link = variant.url;
  const message = applyTemplateValues(
    socialTemplate.value,
    productLabelForVariant(variant),
    link,
    "",
    null
  );
  let url = "";

  if (platform === "facebook") {
    url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      link
    )}`;
  } else if (platform === "x") {
    url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      message
    )}`;
  } else if (platform === "linkedin") {
    url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      link
    )}`;
  }

  if (url) {
    window.open(url, "_blank", "noopener");
  }
};

const shareNative = async (variant) => {
  if (!navigator.share) {
    toast.add({
      severity: "warn",
      summary: "Native share unavailable",
      life: 2000,
    });
    return;
  }

  const link = variant.url;
  const productLabel = productLabelForVariant(variant);
  const message = applyTemplateValues(
    socialTemplate.value,
    productLabel,
    link,
    "",
    null
  )
    .replace(link, "")
    .trim();

  try {
    await navigator.share({
      title: productLabel,
      text: `${message}\n\n${link}`,
    });
  } catch (error) {
    if (error?.name === "AbortError") return;
    toast.add({
      severity: "warn",
      summary: "Native share failed",
      life: 2000,
    });
  }
};

const openLink = (url) => {
  window.open(url, "_blank", "noopener");
};

const resetFlow = () => {
  store.resetFlow();
  setStep(1);
  selectedLanguageKey.value = preferredLanguageOption.value?.key;
  if (preferredLanguageOption.value) {
    store.setLanguages(preferredLanguageOption.value.value);
  }
};

watch(
  () => languageOptions.value,
  (options) => {
    const existing = options.find(
      (opt) => opt.value.join("-") === store.pageTwo.selectedLanguages.join("-")
    );
    const fallback = preferredLanguageOption.value || options[0];
    selectedLanguageKey.value = existing ? existing.key : fallback?.key;
    if (!existing && fallback) {
      store.setLanguages(fallback.value);
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => availableProducts.value.map((p) => p.key),
  (keys) => {
    const filtered = store.pageTwo.selectedProducts
      .filter((key) => keys.includes(key))
      .slice(0, store.productLimit);
    if (filtered.length !== store.pageTwo.selectedProducts.length) {
      store.updatePageTwo({ selectedProducts: filtered });
    }
  },
  { immediate: true }
);

watch(
  () => store.pageTwo.phone,
  (val) => {
    store.updatePageTwo({ phone: val });
  }
);

onMounted(() => {
  syncRegionFromRoute();
  uiLanguageSelection.value = store.uiLanguage;
  if (preferredLanguageOption.value) {
    selectedLanguageKey.value = preferredLanguageOption.value.key;
    store.setLanguages(preferredLanguageOption.value.value);
  }
  if (isDev.value) {
    store.pageOne = {
      producerId: '11 2233',
      licenses: ['Alabama'],
      contracting: 'foresters'
    }
    store.pageTwo = {
      selectedProducts: ['life-insurance'],
      name: 'John Doe',
      phone: '1234567890',
      email: 'john.doe@example.com',
      caLicenseNumber: '1234567890',
      selectedLanguages: ['en'],
      narrator: 'male',
      caslAcknowledged: true
    }
  }
});

watch(
  () => route.path,
  () => {
    syncRegionFromRoute();
  }
);

watch(
  () => store.uiLanguage,
  (lang) => {
    uiLanguageSelection.value = lang;
  }
);
</script>

<style>
@media (max-width: 767px) {
  .card-surface:has(.mobile-action-footer) {
    padding-bottom: 5.5rem;
  }

  .mobile-action-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1200;
    margin: 0;
    padding: 1rem 1.5rem;
    border-top: 1px solid rgb(226 232 240);
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 -10px 24px rgba(15, 23, 42, 0.08);
    backdrop-filter: blur(8px);
  }
}

@media (min-width: 1024px) {
  .state-multiselect-panel .p-multiselect-items {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.5rem 1rem;
    column-count: 3;
    column-gap: 1rem;
  }

  .state-multiselect-panel .p-multiselect-item {
    width: 100%;
  }
}
</style>
